export const HERMES_CAPABILITIES = Object.freeze([
  {
    id: "files",
    icon: "file",
    label: "Workspace files",
    desc: "Read, write, and inspect Hermes workspace files.",
    tools: Object.freeze(["read_file", "write_file", "list_files"]),
  },
  {
    id: "documents",
    icon: "doc",
    label: "Documents",
    desc: "Build markdown, Word, PDF, and slide artifacts.",
    tools: Object.freeze(["read_file", "write_file", "list_files", "build_document"]),
  },
  {
    id: "knowledge",
    icon: "kb",
    label: "Knowledge",
    desc: "Search and save reusable Hermes KB notes and proposed skills.",
    tools: Object.freeze(["search_kb", "write_kb_note", "propose_skill"]),
  },
  {
    id: "projects",
    icon: "project",
    label: "Projects",
    desc: "Create and update project docs, boards, and whiteboards from the current project context.",
    tools: Object.freeze(["project_list", "project_get", "project_create", "project_create_item", "project_update_item"]),
  },
  {
    id: "web",
    icon: "web",
    label: "Web",
    desc: "Search the web, fetch public URLs, and find embeddable image assets.",
    tools: Object.freeze(["web_search", "fetch_url", "find_image_assets"]),
  },
  {
    id: "apps_mcp",
    icon: "apps",
    label: "Apps and MCP",
    desc: "Discover and call connected app tools through MCP.",
    tools: Object.freeze(["mcp_list_tools", "mcp_call"]),
  },
  {
    id: "parallel_chats",
    icon: "parallel",
    label: "Parallel research",
    desc: "Coordinate parallel research/chat lanes when an approved chat-spawn executor is available.",
    tools: Object.freeze([]),
    status: "planned_executor",
  },
  {
    id: "cowork",
    icon: "cowork",
    label: "CoWork",
    desc: "Start and coordinate CoWork-style task workspaces when an approved CoWork executor is available.",
    tools: Object.freeze([]),
    status: "planned_executor",
  },
  {
    id: "automations",
    icon: "automation",
    label: "Automations",
    desc: "Create or update automations and workflows when approved automation mutation tools are available.",
    tools: Object.freeze([]),
    status: "planned_executor",
  },
  {
    id: "tools_admin",
    icon: "tools",
    label: "Tool builder",
    desc: "Propose, create, or modify custom tools only after owner/admin approval.",
    tools: Object.freeze([]),
    status: "planned_executor",
  },
  {
    id: "mcp_admin",
    icon: "mcp",
    label: "MCP admin",
    desc: "Configure or spawn MCP servers only when an approved server-side connector is available.",
    tools: Object.freeze([]),
    status: "planned_executor",
  },
  {
    id: "terminal_agents",
    icon: "terminal",
    label: "Terminal agents",
    desc: "Run terminal and sub-agent work only when an approved local executor is available.",
    tools: Object.freeze([]),
    status: "planned_executor",
  },
  {
    id: "memory",
    icon: "memory",
    label: "User memory",
    desc: "Save explicit user-requested memories for future resident context.",
    tools: Object.freeze(["save_memory"]),
  },
]);

export const HERMES_SURFACES = Object.freeze([
  ["chat", "Chat"],
  ["projects", "Projects"],
  ["automations", "Automations"],
  ["workflows", "Workflows"],
  ["cowork", "CoWork"],
  ["tools", "Tools"],
  ["mcp", "MCP"],
]);

export const DEFAULT_HERMES_RESIDENT_PROFILE = Object.freeze({
  enabled: true,
  model_id: "claude-sonnet-4-6",
  capability_ids: Object.freeze(["files", "documents", "knowledge", "projects", "web", "memory"]),
  default_caps: Object.freeze({ step_cap: 0, dollar_cap_cents: 500, tool_call_cap: 0 }),
  surfaces: Object.freeze({
    chat: true,
    projects: true,
    automations: true,
    workflows: true,
    cowork: true,
    tools: true,
    mcp: true,
  }),
  proactive_mode: "manual",
  show_global_tray: true,
  learning: Object.freeze({
    use_user_memory: true,
    capture_memory: true,
    suggest_skills: true,
    auto_create_skills: false,
  }),
});

export const DEFAULT_HERMES_RESIDENT_POLICY = Object.freeze({
  enabled_for_users: true,
  allowed_capability_ids: Object.freeze(["files", "documents", "knowledge", "projects", "web", "memory"]),
  allowed_surfaces: Object.freeze({
    chat: true,
    projects: true,
    automations: true,
    workflows: true,
    cowork: true,
    tools: true,
    mcp: true,
  }),
  groups: Object.freeze([]),
  group_assignments: Object.freeze({}),
  user_overrides: Object.freeze({}),
});

function boundedJson(value, maxChars = 4200) {
  try {
    const text = JSON.stringify(value, null, 2);
    return text.length > maxChars ? text.slice(0, maxChars) + "\n..." : text;
  } catch {
    return "";
  }
}

function compactList(values, formatter, max = 10) {
  if (!Array.isArray(values) || !values.length) return "";
  const items = values.slice(0, max).map(formatter).filter(Boolean);
  const more = values.length > max ? `; +${values.length - max} more` : "";
  return items.join("; ") + more;
}

function clampInt(value, fallback, min, max) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function normalizeBooleanMap(input, defaults) {
  const out = { ...defaults };
  if (input && typeof input === "object" && !Array.isArray(input)) {
    for (const key of Object.keys(defaults)) {
      if (input[key] !== undefined) out[key] = !!input[key];
    }
  }
  return out;
}

function normalizePartialBooleanMap(input) {
  const defaults = DEFAULT_HERMES_RESIDENT_PROFILE.surfaces;
  const out = {};
  if (input && typeof input === "object" && !Array.isArray(input)) {
    for (const key of Object.keys(defaults)) {
      if (input[key] !== undefined) out[key] = !!input[key];
    }
  }
  return out;
}

function normalizeCapabilityList(input) {
  const validCaps = new Set(HERMES_CAPABILITIES.map((c) => c.id));
  return [...new Set((Array.isArray(input) ? input : []).filter((id) => typeof id === "string" && validCaps.has(id)))];
}

function safePolicyId(input, fallback) {
  const raw = typeof input === "string" ? input.trim().toLowerCase() : "";
  const cleaned = raw.replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64);
  return cleaned || fallback;
}

function normalizePolicyGroup(input, index) {
  const src = input && typeof input === "object" && !Array.isArray(input) ? input : {};
  const id = safePolicyId(src.id || src.name, "group-" + String(index + 1).padStart(2, "0"));
  return {
    id,
    name: typeof src.name === "string" && src.name.trim() ? src.name.trim().slice(0, 80) : "Group " + (index + 1),
    enabled: src.enabled !== false,
    allowed_capability_ids: normalizeCapabilityList(src.allowed_capability_ids),
    denied_capability_ids: normalizeCapabilityList(src.denied_capability_ids),
    allowed_surfaces: normalizePartialBooleanMap(src.allowed_surfaces),
  };
}

function normalizePolicyOverride(input) {
  const src = input && typeof input === "object" && !Array.isArray(input) ? input : {};
  const out = {};
  if (typeof src.enabled === "boolean") out.enabled = src.enabled;
  if (Array.isArray(src.allowed_capability_ids)) out.allowed_capability_ids = normalizeCapabilityList(src.allowed_capability_ids);
  if (Array.isArray(src.denied_capability_ids)) out.denied_capability_ids = normalizeCapabilityList(src.denied_capability_ids);
  const surfaces = normalizePartialBooleanMap(src.allowed_surfaces);
  if (Object.keys(surfaces).length) out.allowed_surfaces = surfaces;
  return out;
}

export function toolsForHermesCapabilities(ids, registeredTools) {
  const allowed = Array.isArray(registeredTools) ? new Set(registeredTools) : null;
  const selected = new Set(Array.isArray(ids) ? ids : []);
  const out = [];
  const seen = new Set();
  for (const cap of HERMES_CAPABILITIES) {
    if (!selected.has(cap.id)) continue;
    for (const tool of cap.tools) {
      if (allowed && !allowed.has(tool)) continue;
      if (seen.has(tool)) continue;
      seen.add(tool);
      out.push(tool);
    }
  }
  return out;
}

export function normalizeHermesResidentProfile(input) {
  const src = input && typeof input === "object" && !Array.isArray(input) ? input : {};
  const defaults = DEFAULT_HERMES_RESIDENT_PROFILE;
  const validCaps = new Set(HERMES_CAPABILITIES.map((c) => c.id));
  const hasCapInput = Array.isArray(src.capability_ids);
  const caps = hasCapInput
    ? src.capability_ids.filter((id) => typeof id === "string" && validCaps.has(id))
    : defaults.capability_ids.slice();
  const dedupedCaps = [...new Set(caps)];
  const rawCaps = src.default_caps && typeof src.default_caps === "object" ? src.default_caps : {};
  const learningInput = src.learning && typeof src.learning === "object" ? src.learning : {};

  return {
    enabled: src.enabled !== false,
    model_id: typeof src.model_id === "string" && src.model_id.trim()
      ? src.model_id.trim()
      : defaults.model_id,
    capability_ids: hasCapInput ? dedupedCaps : defaults.capability_ids.slice(),
    default_caps: {
      step_cap: clampInt(rawCaps.step_cap, defaults.default_caps.step_cap, 0, 1000000),
      dollar_cap_cents: clampInt(rawCaps.dollar_cap_cents, defaults.default_caps.dollar_cap_cents, 1, 1000000),
      tool_call_cap: clampInt(rawCaps.tool_call_cap, defaults.default_caps.tool_call_cap, 0, 1000000),
    },
    surfaces: normalizeBooleanMap(src.surfaces, defaults.surfaces),
    proactive_mode: src.proactive_mode === "suggest" ? "suggest" : "manual",
    show_global_tray: src.show_global_tray !== false,
    learning: {
      use_user_memory: learningInput.use_user_memory !== undefined ? !!learningInput.use_user_memory : defaults.learning.use_user_memory,
      capture_memory: learningInput.capture_memory !== undefined ? !!learningInput.capture_memory : defaults.learning.capture_memory,
      suggest_skills: learningInput.suggest_skills !== undefined ? !!learningInput.suggest_skills : defaults.learning.suggest_skills,
      auto_create_skills: false,
    },
  };
}

export function normalizeHermesResidentPolicy(input) {
  const src = input && typeof input === "object" && !Array.isArray(input) ? input : {};
  const defaults = DEFAULT_HERMES_RESIDENT_POLICY;
  const hasCapInput = Array.isArray(src.allowed_capability_ids);
  const capSource = hasCapInput ? src.allowed_capability_ids : defaults.allowed_capability_ids.slice();
  const groups = (Array.isArray(src.groups) ? src.groups : [])
    .slice(0, 30)
    .map((group, index) => normalizePolicyGroup(group, index));
  const groupIds = new Set(groups.map((group) => group.id));
  const assignments = {};
  const rawAssignments = src.group_assignments && typeof src.group_assignments === "object" && !Array.isArray(src.group_assignments)
    ? src.group_assignments
    : {};
  for (const [userId, ids] of Object.entries(rawAssignments).slice(0, 500)) {
    if (typeof userId !== "string" || !userId.trim()) continue;
    const cleanIds = [...new Set((Array.isArray(ids) ? ids : []).filter((id) => typeof id === "string" && groupIds.has(id)))];
    if (cleanIds.length) assignments[userId.trim().slice(0, 120)] = cleanIds.slice(0, 20);
  }
  const overrides = {};
  const rawOverrides = src.user_overrides && typeof src.user_overrides === "object" && !Array.isArray(src.user_overrides)
    ? src.user_overrides
    : {};
  for (const [userId, override] of Object.entries(rawOverrides).slice(0, 500)) {
    if (typeof userId !== "string" || !userId.trim()) continue;
    const normalized = normalizePolicyOverride(override);
    if (Object.keys(normalized).length) overrides[userId.trim().slice(0, 120)] = normalized;
  }
  const out = {
    enabled_for_users: src.enabled_for_users !== false,
    allowed_capability_ids: normalizeCapabilityList(capSource),
    allowed_surfaces: normalizeBooleanMap(src.allowed_surfaces, defaults.allowed_surfaces),
    groups,
    group_assignments: assignments,
    user_overrides: overrides,
  };
  if (typeof src.updated_at === "string") out.updated_at = src.updated_at;
  if (typeof src.updated_by === "string") out.updated_by = src.updated_by;
  return out;
}

export function buildHermesResidentFirstMessage({ prompt, surfaceContext, memoryEntries, profile, attachments }) {
  const lines = [];
  const p = String(prompt || "").trim();
  if (p) lines.push(p);
  const ctx = surfaceContext && typeof surfaceContext === "object" ? surfaceContext : null;
  if (ctx) {
    lines.push("");
    lines.push("Current platform context:");
    if (ctx.surface) lines.push("- Surface: " + ctx.surface);
    if (ctx.title) lines.push("- Title: " + ctx.title);
    if (ctx.detail) lines.push("- Detail: " + ctx.detail);
    if (ctx.url) lines.push("- URL: " + ctx.url);
    if (ctx.ids && typeof ctx.ids === "object") lines.push("- IDs: " + boundedJson(ctx.ids, 900).replace(/\n/g, " "));
    const resources = compactList(ctx.resources, (r) => {
      if (!r || typeof r !== "object") return "";
      return `${r.kind || "item"}:${r.title || r.name || r.id || "untitled"}${r.id ? ` (${r.id})` : ""}`;
    }, 12);
    if (resources) lines.push("- Visible resources: " + resources);
    if (ctx.recent) lines.push("- Recent context: " + ctx.recent);
    const actions = compactList(ctx.actions, (a) => String(a || "").slice(0, 240), 8);
    if (actions) lines.push("- Platform action hints: " + actions);
    const structured = boundedJson(ctx);
    if (structured) {
      lines.push("");
      lines.push("Structured surface context JSON:");
      lines.push("```json");
      lines.push(structured);
      lines.push("```");
    }
  }
  const attached = Array.isArray(attachments) ? attachments.filter((a) => a && typeof a === "object") : [];
  if (attached.length) {
    lines.push("");
    lines.push("Attached files for this run:");
    for (const att of attached.slice(0, 12)) {
      const name = String(att.name || att.original_path || "attachment").slice(0, 180);
      const original = String(att.original_path || "").slice(0, 240);
      const companion = String(att.companion_path || "").slice(0, 240);
      const size = Number.isFinite(Number(att.size)) ? `${Math.round(Number(att.size) / 1024)}KB` : "";
      lines.push(`- ${name}${size ? ` (${size})` : ""}${original ? `: original=${original}` : ""}${companion ? `; readable_notes=${companion}` : ""}`);
    }
    lines.push("");
    lines.push("Attachment workflow:");
    lines.push("- Call list_files to inspect the workspace and read_file on readable_notes companion files before editing or summarizing uploaded documents.");
    lines.push("- Original uploaded binaries are stored under uploads/. Use read_file on the original only when the binary/base64 content is actually needed.");
    lines.push("- For requested edits to Word/PDF/source documents, produce a revised downloadable artifact with build_document, normally format=docx unless the user requests another format. Preserve headings, tables, labels, and blank-field structure from the readable notes as closely as markdown allows.");
    lines.push("- If an uploaded photo includes an image preview/data URL in readable_notes and the user asks for pictures in the output, place the relevant Markdown image block in content_markdown before calling build_document.");
    lines.push("- If the user asks for new photos or mission images, use find_image_assets when available and embed returned direct_url values as Markdown image blocks. Do not only list references when an embeddable image URL is available.");
  }
  const wantsMemory = profile?.learning?.use_user_memory !== false;
  if (wantsMemory && Array.isArray(memoryEntries) && memoryEntries.length) {
    lines.push("");
    lines.push("Useful saved user memory:");
    for (const entry of memoryEntries.slice(-8)) {
      const text = typeof entry === "string" ? entry : entry?.text;
      if (text) lines.push("- " + String(text).slice(0, 300));
    }
  }
  if (profile?.learning?.capture_memory) {
    lines.push("");
    lines.push("When the user clearly asks you to remember a durable preference or fact, use save_memory if it is available; otherwise mention the proposed memory in your final answer instead of silently saving it.");
  }
  lines.push("");
  lines.push("Execution contract:");
  lines.push("- Treat this as resident platform work, not a single chat reply.");
  lines.push("- Respect the owner/admin Hermes policy and only use capabilities that are available in this run's allowed tools.");
  lines.push("- Create durable workspace artifacts with write_file or build_document when the request asks for research, documentation, analysis, plans, images, or exports.");
  lines.push("- When the request includes uploaded files, ground the work in those attachments and give the user back a downloadable revised document when they ask for document changes.");
  lines.push("- For document changes, final success means a new workspace artifact exists; a prose description alone is incomplete unless the user explicitly asks for advice only.");
  lines.push("- When a reusable fact, source note, project convention, or procedural lesson should help future Hermes runs, use write_kb_note if it is available; keep KB notes concise, source-backed, and free of secrets.");
  lines.push("- When the user asks for a paper, report, memo, or client-ready documentation, produce a Word document artifact when build_document/docx is available.");
  lines.push("- If the current surface is Projects and there is no project_id, but the user asks to create one or more projects, call project_create for each named project first; the current signed-in user becomes owner. Then call project_create_item for each requested doc, board, or whiteboard in the new project ids.");
  lines.push("- If the current surface context includes a project_id and project tools are available, create or update real project docs, boards, and whiteboards with project_create_item/project_update_item instead of only describing what the user should do.");
  lines.push("- For research work, use web_search and fetch_url when available, cite sources, and use find_image_assets for requested photos/figures so final documents can embed actual images instead of only source references.");
  if (profile?.learning?.suggest_skills) {
    lines.push("If the work reveals a reusable procedure, call propose_skill when it is available; otherwise end with a short suggested skill title and why it should be promoted.");
  }
  return lines.join("\n").trim();
}
